import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import { Button, ButtonWithIcon } from '@els/els-react--button';
import { Cell as CellSwitch, Row as TableRow } from '@els/els-react--table';
import ConditionsSubstanceLabel from './ConditionsSubstanceLabel';
import { trackMyProjectsReacted, trackMyProjectsUnreacted } from '../analytics';

const NO_REAGENTS_REPORTED = 'No reagents/catalyst reported';
const NO_SOLVENT_REPORTED = 'No solvent reported';
const YIELD_NOT_REPORTED = 'Yield not reported';
const NO_FUNCTIONAL_GROUP_DATA_FOR_CONDITIONS = 'No data for these conditions';
const FG_COLLAPSED_LIMIT = 2;
const FG_TOGGLE_THRESHOLD = 3;
const idPropType = PropTypes.oneOfType([PropTypes.string, PropTypes.number]);

function formatPercent(value) {
  return value == null ? '' : `${Math.round(value)}%`;
}

function formatCount(value) {
  return Number(value || 0).toLocaleString('en-US');
}

function getFgItemKey(item) {
  return [
    item.fgId ?? item.label,
    item.reactedCount,
    item.unreactedCount,
    ...(item.reactedReactionIds || []),
    ...(item.unreactedReactionIds || []),
  ].join('-');
}

function getSortableReactedPercent(item) {
  return item.reactedPercent == null ? Number.NEGATIVE_INFINITY : item.reactedPercent;
}

function sortFunctionalGroups(items = []) {
  return [...items].sort((firstItem, secondItem) => {
    const reactedPercentDifference = getSortableReactedPercent(secondItem) - getSortableReactedPercent(firstItem);

    if (reactedPercentDifference !== 0) {
      return reactedPercentDifference;
    }

    return (firstItem.label || '').localeCompare(secondItem.label || '', undefined, { sensitivity: 'base' });
  });
}

function FgPercentLink({ item, type, focusTargetId, onOpenReactions }) {
  const percent = type === 'reacted' ? item.reactedPercent : item.unreactedPercent;
  const reactionIds = type === 'reacted' ? item.reactedReactionIds : item.unreactedReactionIds;
  const count = type === 'reacted' ? item.reactedCount : item.unreactedCount;
  const text = formatPercent(percent);

  if (!reactionIds?.length && !count) {
    return <span className="rx-fg-item__percent">{text}</span>;
  }

  return (
    <Button
      id={focusTargetId}
      data-e2e={`conditions-fg-${type}-link`}
      data-testid={`conditions-fg-${type}-link`}
      type="link"
      linkOptions={['inline', 'navigation']}
      className="rx-fg-item__percent"
      aria-label={`Open reactions where ${item.label} ${type}, ${text}`}
      onClick={() => {
        (type === 'reacted' ? trackMyProjectsReacted : trackMyProjectsUnreacted)();
        onOpenReactions(
          {
            fgId: item.fgId,
            label: item.label,
            rank: item.rank,
            type,
            reactionIds,
          },
          focusTargetId
        );
      }}>
      {text}
    </Button>
  );
}

FgPercentLink.propTypes = {
  item: PropTypes.shape({
    fgId: idPropType,
    label: PropTypes.string,
    rank: idPropType,
    reactedPercent: PropTypes.number,
    unreactedPercent: PropTypes.number,
    reactedCount: PropTypes.number,
    unreactedCount: PropTypes.number,
    reactedReactionIds: PropTypes.arrayOf(idPropType),
    unreactedReactionIds: PropTypes.arrayOf(idPropType),
  }).isRequired,
  type: PropTypes.oneOf(['reacted', 'unreacted']).isRequired,
  focusTargetId: PropTypes.string.isRequired,
  onOpenReactions: PropTypes.func.isRequired,
};

function FgLability({
  items,
  rowRank,
  isExpanded = false,
  onExpandedChange = () => null,
  onOpenReactions,
  emptyMessage = NO_FUNCTIONAL_GROUP_DATA_FOR_CONDITIONS,
}) {
  const sortedItems = useMemo(() => sortFunctionalGroups(items), [items]);
  const shouldShowToggle = sortedItems.length > FG_TOGGLE_THRESHOLD;
  const visibleItems = shouldShowToggle && !isExpanded ? sortedItems.slice(0, FG_COLLAPSED_LIMIT) : sortedItems;

  if (!items?.length) {
    return <MissingCell>{emptyMessage}</MissingCell>;
  }

  return (
    <div className="rx-fg-list">
      {visibleItems.map((item, index) => {
        const reactedFocusId = `conditions-fg-reacted-${rowRank}-${index}`;
        const unreactedFocusId = `conditions-fg-unreacted-${rowRank}-${index}`;
        return (
          <div key={getFgItemKey(item)} className="rx-fg-item">
            <span className="u-els-font-family-serif-italic">{item.label} - reacted </span>
            <FgPercentLink
              item={{ ...item, rank: rowRank }}
              type="reacted"
              focusTargetId={reactedFocusId}
              onOpenReactions={onOpenReactions}
            />{' '}
            <span className="u-els-font-family-serif-italic">unreacted </span>
            <FgPercentLink
              item={{ ...item, rank: rowRank }}
              type="unreacted"
              focusTargetId={unreactedFocusId}
              onOpenReactions={onOpenReactions}
            />
            <span className="u-els-font-family-serif-italic"> (total {formatCount(item.total)})</span>
          </div>
        );
      })}
      {shouldShowToggle && (
        <ButtonWithIcon
          data-e2e="conditions-fg-toggle"
          data-testid="conditions-fg-toggle"
          sprite={isExpanded ? 'ChevronUp' : 'ChevronDown'}
          type="link"
          iconRight
          className="rx-fg-list__toggle"
          aria-expanded={isExpanded}
          onClick={() => onExpandedChange(!isExpanded)}>
          {isExpanded ? 'Hide other groups' : 'Show other groups'}
        </ButtonWithIcon>
      )}
    </div>
  );
}

FgLability.propTypes = {
  items: PropTypes.arrayOf(PropTypes.shape({ label: PropTypes.string, total: PropTypes.number })),
  rowRank: idPropType,
  isExpanded: PropTypes.bool,
  onExpandedChange: PropTypes.func,
  onOpenReactions: PropTypes.func.isRequired,
  emptyMessage: PropTypes.string,
};

function MissingCell({ children }) {
  return <span className="rx-cell-placeholder">{children}</span>;
}

MissingCell.propTypes = {
  children: PropTypes.node.isRequired,
};

function SubstanceCell({ items, dataE2e, itemClassName, emptyMessage, linkIdPrefix, onBeforeNavigate }) {
  if (!items?.length) {
    return <MissingCell>{emptyMessage}</MissingCell>;
  }

  return items.map((item, i, arr) => {
    const id = `${linkIdPrefix}-${i}`;
    return (
      <span key={item.id || item.name} className={itemClassName}>
        <ConditionsSubstanceLabel item={item} id={id} dataE2e={dataE2e} onBeforeNavigate={() => onBeforeNavigate(id)} />
        {i < arr.length - 1 && ', '}
      </span>
    );
  });
}

SubstanceCell.propTypes = {
  items: PropTypes.arrayOf(PropTypes.shape({ id: idPropType, name: PropTypes.string })),
  dataE2e: PropTypes.string.isRequired,
  itemClassName: PropTypes.string,
  emptyMessage: PropTypes.string.isRequired,
  linkIdPrefix: PropTypes.string.isRequired,
  onBeforeNavigate: PropTypes.func.isRequired,
};

function ConditionsDetailsTableRow({
  row,
  rowIndex,
  rowId = undefined,
  functionalGroupsExpanded = false,
  onFunctionalGroupsExpandedChange = () => null,
  openExamples,
  openFunctionalGroupReactions,
  onBeforeNavigate,
}) {
  const examplesAnchorId = `conditions-examples-${row.rank}`;

  return (
    <TableRow id={rowId}>
      <CellSwitch cellId="rank" rowIndex={rowIndex} colIndex={0}>
        {row.rank}
      </CellSwitch>
      <th
        scope="row"
        className="conditions-detail__table-row-header table__cell c-els-table__cell c-els-table__element-cell"
        data-col="1"
        data-row={rowIndex}
        data-testid="reagentsCatalysts-semantic-element-cell">
        <SubstanceCell
          items={row.reagentsCatalysts}
          dataE2e="conditions-reagent-link"
          emptyMessage={NO_REAGENTS_REPORTED}
          linkIdPrefix={`conditions-reagent-${row.rank}`}
          onBeforeNavigate={onBeforeNavigate}
        />
      </th>
      <CellSwitch cellId="solvents" rowIndex={rowIndex} colIndex={2}>
        <SubstanceCell
          items={row.solvents}
          dataE2e="stage-solvent-link"
          itemClassName="stage-solvents"
          emptyMessage={NO_SOLVENT_REPORTED}
          linkIdPrefix={`conditions-solvent-${row.rank}`}
          onBeforeNavigate={onBeforeNavigate}
        />
      </CellSwitch>
      <CellSwitch cellId="rcLevel" rowIndex={rowIndex} colIndex={3}>
        <span className="rx-rc-level-cell">
          <span className={`rx-rc-dot rx-rc-dot--${row.rcLevel}`} aria-hidden="true" />
          {row.rcLevel ? row.rcLevel.charAt(0).toUpperCase() + row.rcLevel.slice(1) : ''}
        </span>
      </CellSwitch>
      <CellSwitch cellId="yieldPercent" rowIndex={rowIndex} colIndex={4}>
        {row.yieldPercent == null ? <MissingCell>{YIELD_NOT_REPORTED}</MissingCell> : `${Math.round(row.yieldPercent)}%`}
      </CellSwitch>
      <CellSwitch cellId="reactionCount" rowIndex={rowIndex} colIndex={5}>
        {row.reactionIds.length > 0 ? (
          <ButtonWithIcon
            id={examplesAnchorId}
            data-e2e="conditions-view-examples"
            role="link"
            sprite="ChevronRight"
            type="link"
            size="small"
            iconRight
            aria-label={`View ${row.reactionCount} examples for rank ${row.rank}`}
            onClick={() => openExamples(row.reactionIds, examplesAnchorId)}>
            {row.reactionCount} Examples
          </ButtonWithIcon>
        ) : (
          <span />
        )}
      </CellSwitch>
      <CellSwitch cellId="fgLability" rowIndex={rowIndex} colIndex={6} className="conditions-detail__table-cell-fg">
        <FgLability
          items={row.fgLability}
          rowRank={row.rank}
          isExpanded={functionalGroupsExpanded}
          onExpandedChange={onFunctionalGroupsExpandedChange}
          onOpenReactions={openFunctionalGroupReactions}
        />
      </CellSwitch>
    </TableRow>
  );
}

ConditionsDetailsTableRow.propTypes = {
  row: PropTypes.shape({
    rank: PropTypes.number,
    reagentsCatalysts: PropTypes.arrayOf(PropTypes.shape({})),
    solvents: PropTypes.arrayOf(PropTypes.shape({})),
    reactionCount: PropTypes.number,
    reactionIds: PropTypes.arrayOf(idPropType),
    rcLevel: PropTypes.string,
    yieldPercent: PropTypes.number,
    fgLability: PropTypes.arrayOf(PropTypes.shape({})),
  }).isRequired,
  rowIndex: PropTypes.number.isRequired,
  rowId: PropTypes.string,
  functionalGroupsExpanded: PropTypes.bool,
  onFunctionalGroupsExpandedChange: PropTypes.func,
  openExamples: PropTypes.func.isRequired,
  openFunctionalGroupReactions: PropTypes.func.isRequired,
  onBeforeNavigate: PropTypes.func.isRequired,
};

export default ConditionsDetailsTableRow;
